import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DesktopLabel,
  DropdownContainer,
  DropdownList,
  DropdownListItem,
  DropdownTrigger,
  MobileLabel,
} from './DeviceDropdownStyle';
import { ReactComponent as ArrowSvg } from 'assets/icons/arrow.svg';
import { SelectedDevice } from '../types/Video';

type DeviceDropdownProps = {
  availableDevices: MediaDeviceInfo[];
  selectedDevice: SelectedDevice;
  label: string;
  onSelect: (device: MediaDeviceInfo) => void;
};

const DeviceDropdown: React.FC<DeviceDropdownProps> = ({ availableDevices, selectedDevice, label, onSelect }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleSelect = (device: MediaDeviceInfo) => {
    onSelect(device);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
    } else {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev + 1) % availableDevices.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev - 1 + availableDevices.length) % availableDevices.length);
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          handleSelect(availableDevices[highlightedIndex]);
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          triggerRef.current?.focus();
          break;
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      listRef.current?.children[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, isOpen]);

  const devicesAvailable = availableDevices.length !== 0;

  return (
    <DropdownContainer $label={label}>
      <DesktopLabel>{label}</DesktopLabel>
      <MobileLabel>{t('devices.audio')}</MobileLabel>

      <DropdownTrigger
        as="button"
        ref={triggerRef}
        $devicesAvailable={devicesAvailable}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={(e) => {
          if (!devicesAvailable) return;
          e.stopPropagation();
          toggleDropdown();
        }}
        onKeyDown={handleKeyDown}
      >
        <span>{!devicesAvailable ? t('devices.notFound') : selectedDevice && selectedDevice.label}</span>
        <ArrowSvg />
      </DropdownTrigger>

      {isOpen && (
        <DropdownList role="listbox" ref={listRef} tabIndex={-1}>
          {availableDevices.map((device, index) => (
            <DropdownListItem
              key={device.deviceId}
              role="option"
              aria-selected={highlightedIndex === index}
              onClick={() => handleSelect(device)}
              onMouseEnter={() => setHighlightedIndex(index)}
              style={{ background: highlightedIndex === index ? '#ddd' : 'transparent' }}
            >
              {device.label || t('devices.unnamed')}
            </DropdownListItem>
          ))}
        </DropdownList>
      )}
    </DropdownContainer>
  );
};

export default DeviceDropdown;
